import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { PageHeader, Card, Loading, Empty, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/auth/users").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("All fields are required"); return; }
    setSaving(true);
    try { await api.post("/auth/register", form); toast.success("User created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "staff" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div>
      <PageHeader title="Admin Users" subtitle="Staff who can access the admin panel.">
        <button className={btnPrimary} onClick={() => setOpen(true)} data-testid="add-user-btn"><Plus className="w-4 h-4" /> Add User</button>
      </PageHeader>
      <Card>
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                {["Name", "Email", "Role"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="user-row">
                    <td className="px-5 py-3.5 font-medium text-slate-800 flex items-center gap-2"><UserCog className="w-4 h-4 text-slate-400" /> {u.name}</td>
                    <td className="px-5 py-3.5 text-slate-600">{u.email}</td>
                    <td className="px-5 py-3.5"><span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 capitalize">{u.role}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Admin User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className={labelCls}>Name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><label className={labelCls}>Email</label><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><label className={labelCls}>Password</label><input type="password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div><label className={labelCls}>Role</label><select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="staff">Staff</option><option value="admin">Admin</option></select></div>
          </div>
          <DialogFooter>
            <button className={btnSecondary} onClick={() => setOpen(false)}>Cancel</button>
            <button className={btnPrimary} onClick={save} disabled={saving} data-testid="save-user-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
